// Memorystore (Redis) adapter for RealtimePublisher — BL-1406 Step 1b, ADR 046.
// Server-only. Publishes onto `v2:<topic>`; the realtime gateway holds a single
// `PSUBSCRIBE v2:*` and fans each message out to its socket.io room.

import type { RealtimePublisher } from '../../types/realtime/publisher';
import type { PublishResult } from '../../types/realtime/types';
import { topicToChannel } from '../../isomorphic/realtime/channel-naming';
import { redactTopicSecret } from '../../isomorphic/realtime/topics';

/**
 * The minimum of `ioredis` this adapter uses.
 *
 * Declared structurally rather than importing `Redis` so `@auctogon/core` does
 * not take a hard dependency on the client for a type — the caller supplies a
 * connected instance, and the suite supplies a two-line fake.
 */
export interface RedisPublishClient {
    publish(channel: string, message: string): Promise<number>;
}

/** Wire envelope for one event. */
interface BusEnvelope {
    event: string;
    payload: unknown;
}

/**
 * Creates a RealtimePublisher backed by Redis pub/sub on Memorystore.
 *
 * **Why an envelope.** Supabase's `httpSend(event, payload)` carries the event
 * name in its own slot. Redis `PUBLISH` has exactly two: channel and one string.
 * So the event name rides *inside* the message as `{event, payload}` and the
 * gateway reads it back out. The channel carries the topic and nothing else,
 * which is what lets one `PSUBSCRIBE v2:*` serve every topic.
 *
 * **Fire-and-forget by nature.** Redis pub/sub has no persistence and no
 * delivery guarantee: `PUBLISH` returns the number of subscribers that received
 * it, and zero is a perfectly normal answer when nobody is listening. So a
 * subscriber count is NOT treated as failure — doing that would report an error
 * every time a lot changes with no clerk watching it. Only an actual transport
 * error is a failure.
 *
 * Best-effort like its Supabase peer: failures log via console.error with the
 * Error passed positionally, and return a typed `PublishResult`. **Never
 * throws** — the caller publishes after commit, and a bus outage must not
 * surface as a failed user action.
 *
 * @param redis - A connected Redis client. Caller-supplied so the adapter owns
 *   no lifecycle and can be unit-tested without a live server.
 * @returns RealtimePublisher implementation.
 *
 * @sideEffect Each `publish` sends a Redis `PUBLISH` on `v2:<topic>`. Failures
 *   log the Error positionally, with the topic redacted, and return a typed
 *   PublishResult — never throws.
 */
export function createMemorystoreRealtimePublisher(redis: RedisPublishClient): RealtimePublisher {
    return {
        async publish({ topic, event, payload }): Promise<PublishResult> {
            const channel = topicToChannel(topic);

            // Serialization is separated from transport so the two failure modes
            // stay distinguishable. A payload carrying a BigInt or a cycle is a
            // CALLER bug that no retry fixes; a connection error is the bus being
            // down and is retried by the next publish. Collapsing both into
            // `transport_unavailable` would send someone hunting a network fault
            // that does not exist.
            let message: string;
            try {
                const envelope: BusEnvelope = { event, payload };
                message = JSON.stringify(envelope);
            } catch (err) {
                // Error passed POSITIONALLY, not nested under `err` — the boot
                // console patch promotes a third-arg Error into Cloud Logging's
                // reserved `error.message` / `error.stack` and auto-groups it in
                // Error Reporting. Nested under a plain key it is opaque JSON.
                // No hand-rolled ref id either: `crypto.randomUUID().slice(0,8)`
                // is banned (banned-patterns.md) because it correlates to
                // nothing. The trace id the patch attaches is the correlator, and
                // it is the same one apiHandler surfaces as `requestId`.
                console.error(
                    'realtime publish serialization failed',
                    { topic: redactTopicSecret(topic), event },
                    err,
                );
                return {
                    success: false,
                    error: 'Publish failed',
                    code: 'serialization_failed',
                };
            }

            try {
                await redis.publish(channel, message);
                return { success: true };
            } catch (err) {
                console.error(
                    'realtime publish failed',
                    { topic: redactTopicSecret(topic), event },
                    err,
                );
                return {
                    success: false,
                    error: 'Publish failed',
                    code: 'transport_unavailable',
                };
            }
        },
    };
}
